import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DownOutlined,
  UpOutlined,
  PlusOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Input, InputNumber, Modal, Spin } from 'antd';
import dayjs from 'dayjs';
import { useNotify } from '@/hooks';
import { axiosClient } from '@/libs/axios-client';
import {
  addAdditionalHolidayDays,
  getAdditionalHolidayDays,
} from '@/services/employees.service';
import { QUERY_KEY } from '@/shared/constants';
import { useAuthStore } from '@/store/useAuthStore';

function AdditionalHolidayDaysSection({ employeeId, totalDaysFromServer = 0 }) {
  const user = useAuthStore((state) => state.user);
  const isEmployee =
    user?.roles?.includes('employee') && user?.roles?.length === 1;
  const { t } = useTranslation(['messages', 'forms']);
  const notification = useNotify();
  const queryClient = useQueryClient();

  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState('edit'); // 'edit' | 'preview'
  const [days, setDays] = useState(null);
  const [reason, setReason] = useState('');
  const [generateDocument, setGenerateDocument] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  const resetForm = () => {
    setShowForm(false);
    setStep('edit');
    setDays(null);
    setReason('');
    setGenerateDocument(true);
    setSendEmail(true);
  };

  const currentYear = new Date().getFullYear();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [QUERY_KEY.additionalHolidayDays, employeeId],
    queryFn: () => getAdditionalHolidayDays(employeeId),
    enabled: !!employeeId && expanded,
  });

  const mutation = useMutation({
    mutationFn: (payload) => addAdditionalHolidayDays(employeeId, payload),
    onSuccess: () => {
      notification.success({
        message: t('additionalDaysAdded', { ns: 'messages' }),
      });
      resetForm();
      refetch();
      queryClient.invalidateQueries([QUERY_KEY.employee, employeeId]);
    },
    onError: () => {
      notification.error({
        message: t('error', { ns: 'messages' }),
      });
    },
  });

  const handleConfirm = () => {
    if (!days || days === 0) return;
    mutation.mutate({
      year: currentYear,
      days,
      reason,
      generateDocument,
      sendEmail,
    });
  };

  const entries = data?.entries || [];
  const totalDays = entries.reduce((sum, e) => sum + e.days, 0);

  const handleDownloadDocument = async (documentId) => {
    try {
      const response = await axiosClient.post(
        '/documents/download',
        { documentId, type: 'pdf', returnDoc: true },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'additional-holiday-days.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      notification.error({
        message: t('downloadError', { ns: 'messages' }),
      });
    }
  };

  return (
    <div>
      <div className="flex items-center">
        <div className="mr-2 flex h-8 w-1/2 items-center">
          <label className="w-full text-sm">
            {t('additionalHolidayDays', { ns: 'messages' })}:
          </label>
        </div>
        <div className="flex flex-1 items-center gap-2">
          <Input
            value={expanded ? totalDays : totalDaysFromServer}
            disabled
            className="flex-1"
          />
          <div className="flex items-center gap-1">
            {!isEmployee && (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  setExpanded(true);
                  setStep('edit');
                  setDays(null);
                  setReason('');
                  setGenerateDocument(true);
                  setSendEmail(true);
                  setShowForm(true);
                }}
              >
                {t('addAdditionalDays', { ns: 'messages' })}
              </Button>
            )}
            <button
              type="button"
              className="flex h-8 w-6 items-center justify-center text-zinc-400 hover:text-zinc-600"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <UpOutlined style={{ fontSize: 10 }} />
              ) : (
                <DownOutlined style={{ fontSize: 10 }} />
              )}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="ml-[calc(50%+8px)] mt-1">
          {isLoading ? (
            <div className="flex justify-center py-2">
              <Spin size="small" />
            </div>
          ) : entries.length > 0 ? (
            <ul className="mb-1 divide-y divide-zinc-100">
              {entries.map((entry) => (
                <li
                  key={entry._id}
                  className="flex items-center gap-2 py-1 text-xs"
                >
                  <span className="w-[68px] shrink-0 text-zinc-400">
                    {dayjs(entry.addedAt).format('DD.MM.YYYY')}
                  </span>
                  <span
                    className={`w-6 shrink-0 text-center font-semibold ${entry.days > 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {entry.days > 0 ? `+${entry.days}` : entry.days}
                  </span>
                  <span
                    className="min-w-0 flex-1 truncate text-zinc-500"
                    title={entry.reason}
                  >
                    {entry.reason || '—'}
                  </span>
                  <span
                    className="shrink-0 truncate text-zinc-400"
                    style={{ maxWidth: 80 }}
                    title={
                      entry.addedBy
                        ? `${entry.addedBy.firstName} ${entry.addedBy.lastName}`
                        : ''
                    }
                  >
                    {entry.addedBy
                      ? `${entry.addedBy.firstName} ${entry.addedBy.lastName}`
                      : ''}
                  </span>
                  {entry.document && (
                    <Button
                      type="text"
                      size="small"
                      className="!h-5 !w-5 !min-w-0 !p-0 text-zinc-400 hover:!text-blue-500"
                      icon={<DownloadOutlined style={{ fontSize: 11 }} />}
                      onClick={() => handleDownloadDocument(entry.document)}
                    />
                  )}
                </li>
              ))}
            </ul>
          ) : null}

        </div>
      )}

      <Modal
        open={showForm && !isEmployee}
        title={
          step === 'edit'
            ? t('addAdditionalDays', { ns: 'messages' })
            : t('additionalDaysReviewTitle', { ns: 'messages' })
        }
        onCancel={resetForm}
        footer={
          step === 'edit' ? (
            <>
              <Button onClick={resetForm}>
                {t('cancel', { ns: 'forms' })}
              </Button>
              <Button
                type="primary"
                onClick={() => setStep('preview')}
                disabled={!days || days === 0}
              >
                {t('additionalDaysContinue', { ns: 'messages' })}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setStep('edit')}>
                {t('additionalDaysBack', { ns: 'messages' })}
              </Button>
              <Button
                type="primary"
                loading={mutation.isPending}
                onClick={handleConfirm}
              >
                {t('additionalDaysConfirm', { ns: 'messages' })}
              </Button>
            </>
          )
        }
        maskClosable={false}
        destroyOnClose
      >
        {step === 'edit' ? (
          <div className="flex flex-col gap-3 py-2">
            <div>
              <label className="mb-1 block text-xs text-zinc-500">
                {t('additionalDaysCount', { ns: 'messages' })}
              </label>
              <InputNumber
                value={days}
                onChange={setDays}
                min={1}
                className="!w-32"
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-zinc-500">
                {t('additionalDaysReason', { ns: 'messages' })}
              </label>
              <Input.TextArea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder={t('additionalDaysReason', { ns: 'messages' })}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 py-2">
            <p className="text-xs text-zinc-500">
              {t('additionalDaysReviewSubtitle', { ns: 'messages' })}
            </p>
            <div className="rounded border border-zinc-200 bg-zinc-50 p-3 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-zinc-500">
                  {t('additionalDaysCount', { ns: 'messages' })}:
                </span>
                <span className="font-semibold">{days}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-zinc-500">
                  {t('additionalDaysYear', { ns: 'messages' })}:
                </span>
                <span className="font-semibold">{currentYear}</span>
              </div>
              <div className="py-1">
                <div className="text-zinc-500">
                  {t('additionalDaysReason', { ns: 'messages' })}:
                </div>
                <div className="mt-1 whitespace-pre-wrap break-words text-zinc-700">
                  {reason || '—'}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <Checkbox
                checked={generateDocument}
                onChange={(e) => setGenerateDocument(e.target.checked)}
              >
                {t('additionalDaysGenerateDocument', { ns: 'messages' })}
              </Checkbox>
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              >
                {t('additionalDaysSendEmail', { ns: 'messages' })}
              </Checkbox>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AdditionalHolidayDaysSection;
